import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Logo } from "@/components/brand";
import { ChangePasswordForm } from "@/components/change-password-form";
import { CredentialCard } from "@/components/credential-card";
import { ReasonNotice } from "@/components/password-fields";
import { Showcase } from "@/components/showcase";
import { daysUntilPasswordExpiry, reasonKey } from "@/utils/password-expiry";
import { effectivePolicyQuery, profileQuery } from "@/services/queries";
import { useAuthStore } from "@/store/auth";
import { useTranslation } from "@/store/locale";

/**
 * The password change something else decided on: an administrator's forced
 * change, or an expiry warning acted on.
 *
 * Full screen and outside the shell, because the whole point is that there is
 * nothing else to do first. A change someone chooses to make opens inside the
 * app instead, at `/profile/password`.
 */
function ChangePasswordPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const signOut = useAuthStore((state) => state.signOut);
  const { data: profile } = useQuery(profileQuery());
  const { data: policy } = useQuery(effectivePolicyQuery());
  const days = profile ? daysUntilPasswordExpiry(profile, policy) : null;
  const reason = reasonKey(Boolean(profile?.force_password_change), days);

  // A forced change is a wall: the shell's guard bounces anyone who leaves it
  // straight back, so the only honest way out is to sign out. Anyone else came
  // here with a working session — from the expiry warning — and is entitled to
  // walk away from it and carry on.
  const forced = Boolean(profile?.force_password_change);
  const leave = () => {
    if (!forced) {
      void navigate("/");
      return;
    }
    signOut();
    void navigate("/login");
  };

  return (
    <div className="bg-canvas grid min-h-dvh gap-8 px-5 pt-6 pb-12 sm:px-8 lg:grid-cols-[minmax(0,1fr)_min(571px,42%)] lg:items-stretch lg:gap-10 lg:px-8 lg:py-8">
      <main className="flex flex-col">
        <div className="flex flex-1 items-center py-10 lg:py-6">
          <div className="mx-auto w-full max-w-[32.5rem]">
            <Logo className="mb-10 w-[140px]" />

            <h1 className="text-title-1 text-heading">{t("pw.title")}</h1>
            <p className="text-fg-muted text-body mt-2 text-pretty">{t("pw.subtitle")}</p>

            <div className="mt-8">
              <ChangePasswordForm
                onDone={() => void navigate("/")}
                notice={
                  reason ? (
                    <ReasonNotice>{t(reason).replace("{days}", String(days))}</ReasonNotice>
                  ) : null
                }
                footer={(saving) => (
                  <button
                    type="button"
                    onClick={leave}
                    disabled={saving}
                    className="text-accent text-subhead justify-self-end font-medium underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50"
                  >
                    {forced ? t("reset.back") : t("pw.backHome")}
                  </button>
                )}
              />
            </div>
          </div>
        </div>
      </main>

      {/* The countdown belongs beside the form, where it is a fact about the
          account rather than another line of the form to read. */}
      <div className="hidden lg:block">
        <Showcase
          visual={<CredentialCard days={days} />}
          title={reason ? t("showcase.pwTitle") : t("showcase.pwIdle")}
          subtitle={t("showcase.pwBody")}
        />
      </div>
    </div>
  );
}

export default ChangePasswordPage;
